/**
 * `ActivationFlow` — orchestrator that drives the activation state
 * machine against the three backend clients (identity-proofing,
 * pci-service, consent-artifact).
 *
 * Composition contract:
 *
 *   IIdentityProofingClient — NIST IAL2 vendor handoff (PFO §2.2)
 *   IPCIServiceClient       — PCI issuance (Tech Spec §4.1)
 *   IConsentArtifactClient  — activation-scope artifact under
 *                             patient-rights frame (UC22 + ADR-027
 *                             generic-suite framing)
 *
 * The orchestrator subscribes observers to state changes; views
 * read state through the orchestrator's `getState()` and dispatch
 * intent through the orchestrator's high-level methods (rather
 * than dispatching raw `ActivationEvent`s). This keeps the view
 * layer separated from state-machine bookkeeping.
 *
 * **Doctrinal posture.** The orchestrator does NOT touch the
 * `IEnvelopeCryptoAdapter` or any `IActiveSession` during
 * activation. Activation predates KEK release; the patient session
 * crypto wiring begins at sign-in (claim-queue item 2). No
 * cleartext key material flows through this module by design.
 */
import { applyEvent, initialActivationState, isTerminal, } from './activation-state.js';
import { validateVendorRedirectUrl } from './validate-vendor-redirect-url.js';
// ---------- Implementation ----------
export class ActivationFlow {
    options;
    state = initialActivationState();
    observers = new Set();
    constructor(options) {
        this.options = options;
    }
    getState() {
        return this.state;
    }
    subscribe(observer) {
        this.observers.add(observer);
        observer(this.state);
        return () => {
            this.observers.delete(observer);
        };
    }
    acknowledgeTerms() {
        if (this.state.kind !== 'intro') {
            return;
        }
        this.transition({ kind: 'acknowledge-terms' });
    }
    async startIdentityProofing(opts) {
        if (this.state.kind !== 'terms-acknowledged') {
            return;
        }
        try {
            const session = await this.options.identityProofing.openSession({
                locale: opts?.locale ?? this.options.locale,
                ...(opts?.accommodation !== undefined ? { accommodation: opts.accommodation } : {}),
            });
            // NH5 — boundary validation of vendor-supplied redirect URL per
            // CTO security re-scan delta HIGH finding. Vendor-returned URL
            // crosses substrate-fiduciary trust boundary into PHG document
            // origin; reject non-https schemes (javascript:/data:/etc) +
            // embedded credentials before transitioning to the
            // `identity-proofing-pending` state that renders it as an anchor
            // href. See `validate-vendor-redirect-url.ts` doc-comment for
            // doctrinal anchors.
            const validation = validateVendorRedirectUrl(session.vendorRedirectUrl);
            if (!validation.ok) {
                this.transition({
                    kind: 'error',
                    code: 'identity-proofing-open-failed',
                    message: `vendor-redirect URL validation failed: ${validation.message}`,
                });
                return;
            }
            this.transition({
                kind: 'open-identity-proofing-session',
                sessionId: session.sessionId,
                vendorRedirectUrl: session.vendorRedirectUrl,
            });
        }
        catch (e) {
            this.transition({
                kind: 'error',
                code: 'identity-proofing-open-failed',
                message: errorMessage(e),
            });
        }
    }
    async resolveIdentityProofing() {
        if (this.state.kind !== 'identity-proofing-pending') {
            return;
        }
        const sessionId = this.state.sessionId;
        try {
            const result = await this.options.identityProofing.resolveSession(sessionId);
            this.transition({ kind: 'resolve-identity-proofing', result });
        }
        catch (e) {
            this.transition({
                kind: 'error',
                code: 'identity-proofing-resolve-failed',
                message: errorMessage(e),
            });
        }
    }
    async continueToPCIProvisioning() {
        if (this.state.kind !== 'identity-proofing-resolved') {
            return;
        }
        const attestationToken = this.state.result.attestationToken;
        this.transition({ kind: 'continue-from-identity-proofing-resolved' });
        try {
            const pci = await this.options.pciService.issuePCI({ identityToken: attestationToken });
            this.transition({ kind: 'pci-provisioned', pci });
        }
        catch (e) {
            this.transition({
                kind: 'error',
                code: 'pci-provisioning-failed',
                message: errorMessage(e),
            });
        }
    }
    async continueToConsentArtifactIssuance() {
        if (this.state.kind !== 'pci-provisioned') {
            return;
        }
        const pci = this.state.pci;
        this.transition({ kind: 'start-consent-artifact-issuance' });
        try {
            const artifact = await this.options.consentArtifact.issueActivationArtifact({
                subjectPci: pci.pci,
                locale: this.options.locale,
            });
            this.transition({ kind: 'consent-artifact-issued', artifact });
        }
        catch (e) {
            this.transition({
                kind: 'error',
                code: 'consent-artifact-issuance-failed',
                message: errorMessage(e),
            });
        }
    }
    cancel(reason) {
        if (isTerminal(this.state)) {
            return;
        }
        this.transition({ kind: 'cancel', reason });
    }
    // ---------- private ----------
    transition(event) {
        const next = applyEvent(this.state, event);
        if (next === this.state) {
            return;
        }
        this.state = next;
        for (const observer of this.observers) {
            observer(next);
        }
    }
}
function errorMessage(e) {
    if (e instanceof Error) {
        return e.message;
    }
    return String(e);
}
//# sourceMappingURL=activation-flow.js.map